import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

//////////////////////////////////////////////
// DAILY TRUCK CONFIGURATION
//////////////////////////////////////////////
const DAILY_TRUCK_CONFIG = {
  locationId: 'daily-truck',
  locationName: 'Daily Truck Check',
  tableName: 'daily_truck_checks',
  storageFolder: 'daily_truck'
};

function DailyTruck({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  const [formData, setFormData] = useState({
    ambulance: '',
    oncoming_crew: '',
    dispatch_radio_check: '',
    fuel_card_present: '',
    fuel_level: '',
    def_full: '',
    tire_condition: '',
    odometer_reading: '',
    main_oxygen_level: '',
    stretcher_oxygen_level: '',
    spare_oxygen_1: '',
    spare_oxygen_2: '',
    pediatric_bag_tag: '',
    glucometer_high_test: '',
    glucometer_low_test: '',
    ventilator_number: '',
    ventilator_passed: '',
    zoll_monitor: '',
    defib_30j_test: '',
    iv_pumps_count: '',
    pump_1_number: '',
    pump_2_number: '',
    pump_3_number: '',
    pump_4_number: '',
    jump_bag_present: '',
    stair_chair_battery: '',
    lucas_device_complete: ''
  });

  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // When pump count drops, clear any pump-number slots that are no longer visible
      if (field === 'iv_pumps_count') {
        const count = parseInt(value, 10);
        const visible = Number.isFinite(count) ? count : 0;
        for (let slot = visible + 1; slot <= 4; slot += 1) {
          updated[`pump_${slot}_number`] = '';
        }
      }
      return updated;
    });
  };

  const showAlertPopup = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleSubmit = async () => {
    // Validation
    const requiredFields = [
      { field: 'ambulance', label: 'Ambulance' },
      { field: 'oncoming_crew', label: 'Oncoming Crew' },
      { field: 'dispatch_radio_check', label: 'Radio Check with Dispatch Completed' },
      { field: 'fuel_card_present', label: 'Fuel Card Present' },
      { field: 'fuel_level', label: 'Fuel Level' },
      { field: 'def_full', label: 'DEF Full' },
      { field: 'tire_condition', label: 'Tire Condition' },
      { field: 'odometer_reading', label: 'Odometer Reading' },
      { field: 'main_oxygen_level', label: 'Main Oxygen Level' },
      { field: 'stretcher_oxygen_level', label: 'Stretcher Oxygen Level' },
      { field: 'spare_oxygen_1', label: 'Spare Oxygen Cylinder 1' },
      { field: 'spare_oxygen_2', label: 'Spare Oxygen Cylinder 2' },
      { field: 'pediatric_bag_tag', label: 'Pediatric Bag Tag #' },
      { field: 'glucometer_high_test', label: 'Glucometer High Test Reading' },
      { field: 'glucometer_low_test', label: 'Glucometer Low Test Reading' },
      { field: 'ventilator_number', label: 'Ventilator #' },
      { field: 'ventilator_passed', label: 'Ventilator Passed Daily Check' },
      { field: 'zoll_monitor', label: 'Zoll Monitor Functioning w/ Supplies' },
      { field: 'defib_30j_test', label: 'Defib 30J Test' },
      { field: 'iv_pumps_count', label: '# of IV Pumps' },
      { field: 'jump_bag_present', label: 'Jump Bag Present' },
      { field: 'stair_chair_battery', label: 'Checked Stair Chair Battery' },
      { field: 'lucas_device_complete', label: 'LUCAS Device Complete' }
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field]) {
        showAlertPopup(`${label} is required.`);
        return;
      }
    }

    // Conditional: require one pump-number entry per pump on the truck
    const pumpCount = parseInt(formData.iv_pumps_count, 10) || 0;
    for (let slot = 1; slot <= pumpCount; slot += 1) {
      if (!formData[`pump_${slot}_number`]) {
        showAlertPopup(`Pump ${slot} Number is required.`);
        return;
      }
    }

    setLoading(true);

    try {
      const submissionData = {
        ...formData,
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        user_name: `${userInfo.firstName} ${userInfo.lastName}`,
        email: userInfo.email,
        created_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from(DAILY_TRUCK_CONFIG.tableName)
        .insert([submissionData]);

      if (error) throw error;

      showAlertPopup('Daily truck check submitted successfully!', 'success');

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          ambulance: '',
          oncoming_crew: '',
          dispatch_radio_check: '',
          fuel_card_present: '',
          fuel_level: '',
          def_full: '',
          tire_condition: '',
          odometer_reading: '',
          main_oxygen_level: '',
          stretcher_oxygen_level: '',
          spare_oxygen_1: '',
          spare_oxygen_2: '',
          pediatric_bag_tag: '',
          glucometer_high_test: '',
          glucometer_low_test: '',
          ventilator_number: '',
          ventilator_passed: '',
          zoll_monitor: '',
          defib_30j_test: '',
          iv_pumps_count: '',
          pump_1_number: '',
          pump_2_number: '',
          pump_3_number: '',
          pump_4_number: '',
          jump_bag_present: '',
          stair_chair_battery: '',
          lucas_device_complete: ''
        });
      }, 1500);
    } catch (err) {
      console.error('Error submitting:', err);
      showAlertPopup(`Error: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '20px' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      {/* Main Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', paddingTop: '20px' }}>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            color: '#0E3293',
            fontSize: '32px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            Daily Truck Check Form
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            {userInfo.firstName} {userInfo.lastName}
          </p>
        </div>

        {/* Form */}
        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '15px',
          padding: '40px',
          marginBottom: '30px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>

          {/* Ambulance */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Ambulance <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Which ambulance are you completing the daily rig check for?
            </p>
            <select
              className="form-input"
              value={formData.ambulance}
              onChange={(e) => handleInputChange('ambulance', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Mayo 21">Mayo 21</option>
              <option value="Mayo 22">Mayo 22</option>
              <option value="Mayo 23">Mayo 23</option>
              <option value="Mayo 24">Mayo 24</option>
              <option value="Mayo 25">Mayo 25</option>
            </select>
          </div>

          {/* Oncoming Crew */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Oncoming Crew <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Please enter the last names of each crew member.
            </p>
            <input
              type="text"
              className="form-input"
              value={formData.oncoming_crew}
              onChange={(e) => handleInputChange('oncoming_crew', e.target.value)}
              placeholder="Enter crew member names"
            />
          </div>

          {/* Radio Check with Dispatch */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Radio Check with Dispatch Completed <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="dispatch_radio_check"
                  value="Yes"
                  checked={formData.dispatch_radio_check === 'Yes'}
                  onChange={(e) => handleInputChange('dispatch_radio_check', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="dispatch_radio_check"
                  value="No"
                  checked={formData.dispatch_radio_check === 'No'}
                  onChange={(e) => handleInputChange('dispatch_radio_check', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Fuel Card Present */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Fuel Card Present? <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="fuel_card"
                  value="Yes"
                  checked={formData.fuel_card_present === 'Yes'}
                  onChange={(e) => handleInputChange('fuel_card_present', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="fuel_card"
                  value="No"
                  checked={formData.fuel_card_present === 'No'}
                  onChange={(e) => handleInputChange('fuel_card_present', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Fuel Level */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Fuel Level <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-input"
              value={formData.fuel_level}
              onChange={(e) => handleInputChange('fuel_level', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Full">Full</option>
              <option value="3/4">3/4</option>
              <option value="1/2">1/2</option>
              <option value="1/4">1/4</option>
              <option value="Empty">Empty</option>
            </select>
          </div>

          {/* DEF Full */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              DEF Full? <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-input"
              value={formData.def_full}
              onChange={(e) => handleInputChange('def_full', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="N/A">N/A</option>
            </select>
          </div>

          {/* Tire Condition */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Tire Condition <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              If any of the tires are found in "poor" condition, please notify supervisor immediately.
            </p>
            <select
              className="form-input"
              value={formData.tire_condition}
              onChange={(e) => handleInputChange('tire_condition', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
            </select>
          </div>

          {/* Odometer Reading */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Odometer Reading <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="number"
              className="form-input"
              value={formData.odometer_reading}
              onChange={(e) => handleInputChange('odometer_reading', e.target.value)}
              placeholder="Enter odometer reading"
            />
          </div>

          {/* Oxygen Levels */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Main Oxygen Level <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.main_oxygen_level}
              onChange={(e) => handleInputChange('main_oxygen_level', e.target.value)}
              placeholder="Enter level"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Stretcher Oxygen Level <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.stretcher_oxygen_level}
              onChange={(e) => handleInputChange('stretcher_oxygen_level', e.target.value)}
              placeholder="Enter level"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Spare Oxygen Cylinder 1 <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.spare_oxygen_1}
              onChange={(e) => handleInputChange('spare_oxygen_1', e.target.value)}
              placeholder="Enter level"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Spare Oxygen Cylinder 2 <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.spare_oxygen_2}
              onChange={(e) => handleInputChange('spare_oxygen_2', e.target.value)}
              placeholder="Enter level"
            />
          </div>

          {/* Pediatric Bag Tag */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Pediatric Bag Tag # <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.pediatric_bag_tag}
              onChange={(e) => handleInputChange('pediatric_bag_tag', e.target.value)}
              placeholder="Enter tag number"
            />
          </div>

          {/* Glucometer Tests */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Glucometer High Test Reading <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Enter the actual value displayed on the glucometer for the high control test.
            </p>
            <input
              type="text"
              className="form-input"
              value={formData.glucometer_high_test}
              onChange={(e) => handleInputChange('glucometer_high_test', e.target.value)}
              placeholder="Enter high test reading"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Glucometer Low Test Reading <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Enter the actual value displayed on the glucometer for the low control test.
            </p>
            <input
              type="text"
              className="form-input"
              value={formData.glucometer_low_test}
              onChange={(e) => handleInputChange('glucometer_low_test', e.target.value)}
              placeholder="Enter low test reading"
            />
          </div>

          {/* Ventilator */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Ventilator # <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.ventilator_number}
              onChange={(e) => handleInputChange('ventilator_number', e.target.value)}
              placeholder="Enter ventilator number"
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Ventilator Passed Daily Check <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="ventilator_passed"
                  value="Yes"
                  checked={formData.ventilator_passed === 'Yes'}
                  onChange={(e) => handleInputChange('ventilator_passed', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="ventilator_passed"
                  value="No"
                  checked={formData.ventilator_passed === 'No'}
                  onChange={(e) => handleInputChange('ventilator_passed', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Zoll Monitor */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Zoll Monitor Functioning w/ Supplies <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="zoll_monitor"
                  value="Yes"
                  checked={formData.zoll_monitor === 'Yes'}
                  onChange={(e) => handleInputChange('zoll_monitor', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="zoll_monitor"
                  value="No"
                  checked={formData.zoll_monitor === 'No'}
                  onChange={(e) => handleInputChange('zoll_monitor', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Defib Test */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Defib 30J Test <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-input"
              value={formData.defib_30j_test}
              onChange={(e) => handleInputChange('defib_30j_test', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>

          {/* IV Pumps */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              # of IV Pumps <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Total number of pumps on the truck (each ambulance should have three).
            </p>
            <select
              className="form-input"
              value={formData.iv_pumps_count}
              onChange={(e) => handleInputChange('iv_pumps_count', e.target.value)}
            >
              <option value="">-- Select one</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
            </select>
          </div>

          {/* Pump Numbers — only render a slot per pump on the truck */}
          {Array.from(
            { length: parseInt(formData.iv_pumps_count, 10) || 0 },
            (_, i) => i + 1
          ).map((slot) => {
            const field = `pump_${slot}_number`;
            return (
              <div key={field} style={{ marginBottom: '25px' }}>
                <label className="form-label">
                  Pump {slot} Number <span style={{ color: 'red' }}>*</span>
                </label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                  Select the pump number (1-30) currently in this slot.
                </p>
                <select
                  className="form-input"
                  value={formData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                >
                  <option value="">-- Select one</option>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={String(num)}>{num}</option>
                  ))}
                  <option value="N/A">N/A</option>
                </select>
              </div>
            );
          })}

          {/* Jump Bag */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Jump Bag Present <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="jump_bag"
                  value="Yes"
                  checked={formData.jump_bag_present === 'Yes'}
                  onChange={(e) => handleInputChange('jump_bag_present', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="jump_bag"
                  value="No"
                  checked={formData.jump_bag_present === 'No'}
                  onChange={(e) => handleInputChange('jump_bag_present', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Stair Chair Battery */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              Checked Stair Chair Battery <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="stair_chair"
                  value="Yes"
                  checked={formData.stair_chair_battery === 'Yes'}
                  onChange={(e) => handleInputChange('stair_chair_battery', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="stair_chair"
                  value="No"
                  checked={formData.stair_chair_battery === 'No'}
                  onChange={(e) => handleInputChange('stair_chair_battery', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* LUCAS Device */}
          <div style={{ marginBottom: '25px' }}>
            <label className="form-label">
              LUCAS Device Complete <span style={{ color: 'red' }}>*</span>
            </label>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Device in case, battery installed, spare battery in charger, backboard, patient straps attached, neck strap, suction cup installed, extra suction cup. SWAP LUCAS BATTERIES WITH EVERY RIG CHECK.
            </p>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="lucas_device"
                  value="Yes"
                  checked={formData.lucas_device_complete === 'Yes'}
                  onChange={(e) => handleInputChange('lucas_device_complete', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="lucas_device"
                  value="No"
                  checked={formData.lucas_device_complete === 'No'}
                  onChange={(e) => handleInputChange('lucas_device_complete', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                No
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary btn-full"
            onClick={handleSubmit}
            disabled={loading}
            style={{ padding: '15px', fontSize: '18px', marginTop: '20px' }}
          >
            {loading ? 'Submitting...' : 'Submit Daily Check'}
          </button>
        </div>
      </div>

      {/* Alert Popup */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              backgroundColor: alertType === 'success' ? '#e8f5e8' : '#fee',
              color: alertType === 'success' ? '#2e7d32' : '#c33'
            }}>
              {alertType === 'success' ? '✓' : '✕'}
            </div>
            <h3 style={{
              margin: '0 0 15px',
              fontSize: '20px',
              color: '#333'
            }}>
              {alertType === 'success' ? 'Success' : 'Error'}
            </h3>
            <p style={{
              margin: '0 0 25px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {alertMessage}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyTruck;
